import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GREETING =
  "¡Hola! Mi nombre es Ángel, asistente de BM Grupo Financiero, estoy para servirle. Platíqueme, ¿en qué le puedo apoyar?";

const LEAD_INITIAL = { nombre: "", email: "", telefono: "", mensaje: "" };

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([{ role: "assistant", content: GREETING }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [showLead, setShowLead] = useState(false);
  const [leadSubmitted, setLeadSubmitted] = useState(false);
  const [lead, setLead] = useState(LEAD_INITIAL);
  const [leadLoading, setLeadLoading] = useState(false);
  const scrollRef = useRef(null);
  const idleRef = useRef(null);

  const closeAndReset = () => {
    setOpen(false);
    setMessages([{ role: "assistant", content: GREETING }]);
    setSessionId(null);
    setInput("");
    setLoading(false);
    setShowLead(false);
    setLeadSubmitted(false);
    setLead(LEAD_INITIAL);
    if (idleRef.current) clearTimeout(idleRef.current);
  };

  const toggle = () => {
    if (open) closeAndReset();
    else setOpen(true);
  };

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading, open, showLead]);

  // Share the contact form after 1 minute of inactivity
  useEffect(() => {
    if (idleRef.current) clearTimeout(idleRef.current);
    if (!open || loading || showLead || leadSubmitted) return;
    idleRef.current = setTimeout(() => {
      setShowLead(true);
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            "Si gusta, déjenos sus datos y un asesor de BM Grupo Financiero le contactará a la brevedad. Puede completar este breve formulario 👇",
        },
      ]);
    }, 60000);
    return () => clearTimeout(idleRef.current);
  }, [messages, open, loading, showLead, leadSubmitted]);

  const maybeOfferFormOnFarewell = (reply) => {
    const t = (reply || "").toLowerCase();
    const farewell =
      t.includes("hasta luego") ||
      t.includes("quedamos a sus") ||
      t.includes("gusto atenderle") ||
      t.includes("fue un gusto") ||
      t.includes("a sus órdenes") ||
      t.includes("a sus ordenes");
    if (farewell && !leadSubmitted) {
      setTimeout(() => setShowLead(true), 700);
    }
  };

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/chat`, {
        message: text,
        session_id: sessionId,
      });
      if (data.session_id) setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
      maybeOfferFormOnFarewell(data.reply);
    } catch (err) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            "Disculpe, tuve un problema al responder. Intente de nuevo o escríbanos por WhatsApp al +52 81 2667 2407.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const submitLead = async (e) => {
    e.preventDefault();
    if (!lead.nombre.trim() || !lead.email.trim() || !lead.telefono.trim()) return;
    setLeadLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        nombre: lead.nombre.trim(),
        email: lead.email.trim(),
        telefono: lead.telefono.trim(),
        perfil: "otro",
        mensaje:
          lead.mensaje.trim() || "Contacto solicitado desde el chat de Ángel.",
      });
      setLeadSubmitted(true);
      setShowLead(false);
      setLead(LEAD_INITIAL);
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            "¡Gracias! Hemos recibido sus datos. Un asesor de BM Grupo Financiero le contactará muy pronto. 🙂",
        },
      ]);
    } catch (err) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            "No pude registrar sus datos. Por favor escríbanos por WhatsApp al +52 81 2667 2407.",
        },
      ]);
    } finally {
      setLeadLoading(false);
    }
  };

  const suggestions = [];

  return (
    <>
      {/* Launcher */}
      {!open && (
        <span
          data-testid="chat-launcher-label"
          className="fixed right-5 bottom-40 z-50 bg-foreground text-white text-xs font-medium px-3 py-1.5 rounded-full shadow-md whitespace-nowrap pointer-events-none"
        >
          Chatea con nosotros
        </span>
      )}
      <button
        data-testid="chat-launcher"
        onClick={toggle}
        aria-label="Abrir chat con asistente"
        className="fixed right-5 bottom-24 z-50 h-14 w-14 rounded-full bg-primary text-foreground flex items-center justify-center shadow-lg shadow-black/25 transition-transform hover:scale-105"
      >
        {open ? <X size={26} /> : <MessageCircle size={26} />}
        {!open && (
          <span className="absolute -top-1 -right-1 h-3.5 w-3.5 rounded-full bg-foreground border-2 border-white" />
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            data-testid="chat-panel"
            className="fixed right-5 bottom-40 z-50 w-[calc(100vw-2.5rem)] sm:w-[380px] h-[520px] max-h-[70vh] bg-white border border-border rounded-2xl shadow-2xl shadow-black/20 flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="bg-foreground text-white px-5 py-4 flex items-center gap-3 shrink-0">
              <div className="h-9 w-9 rounded-full bg-primary flex items-center justify-center">
                <span className="font-heading font-extrabold text-foreground text-sm">BM</span>
              </div>
              <div className="leading-tight">
                <p className="font-heading text-sm tracking-tight">Ángel</p>
                <p className="text-[11px] text-white/60 flex items-center gap-1.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#25D366]" /> En línea
                </p>
              </div>
            </div>

            {/* Messages */}
            <div
              ref={scrollRef}
              data-testid="chat-messages"
              className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-secondary/40"
            >
              {messages.map((m, i) => (
                <div
                  key={i}
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap rounded-2xl ${
                      m.role === "user"
                        ? "bg-primary text-primary-foreground rounded-br-sm"
                        : "bg-white border border-border text-foreground rounded-bl-sm"
                    }`}
                  >
                    {m.content}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-white border border-border rounded-2xl rounded-bl-sm px-4 py-3 flex items-center gap-2 text-muted-foreground">
                    <Loader2 size={16} className="animate-spin" />
                    <span className="text-xs">Escribiendo…</span>
                  </div>
                </div>
              )}

              {messages.length === 1 && !loading && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {suggestions.map((s) => (
                    <button
                      key={s}
                      onClick={() => {
                        setInput(s);
                        setTimeout(() => send(), 0);
                      }}
                      className="text-xs px-3 py-1.5 rounded-full border border-border bg-white hover:border-primary hover:bg-primary/10 transition-colors"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}

              {showLead && !leadSubmitted && (
                <form
                  onSubmit={submitLead}
                  data-testid="chat-lead-form"
                  className="bg-white border border-primary/40 rounded-2xl rounded-bl-sm p-4 space-y-2.5 shadow-sm"
                >
                  <p className="text-xs font-bold uppercase tracking-[0.12em] text-foreground/70">
                    Déjenos sus datos
                  </p>
                  <input
                    data-testid="lead-nombre"
                    value={lead.nombre}
                    onChange={(e) => setLead((l) => ({ ...l, nombre: e.target.value }))}
                    placeholder="Nombre completo"
                    required
                    className="w-full h-10 px-3 rounded-lg bg-secondary/60 border border-border text-sm outline-none focus:border-primary"
                  />
                  <input
                    data-testid="lead-telefono"
                    value={lead.telefono}
                    onChange={(e) => setLead((l) => ({ ...l, telefono: e.target.value }))}
                    placeholder="Teléfono / WhatsApp"
                    required
                    className="w-full h-10 px-3 rounded-lg bg-secondary/60 border border-border text-sm outline-none focus:border-primary"
                  />
                  <input
                    data-testid="lead-email"
                    type="email"
                    value={lead.email}
                    onChange={(e) => setLead((l) => ({ ...l, email: e.target.value }))}
                    placeholder="Correo electrónico"
                    required
                    className="w-full h-10 px-3 rounded-lg bg-secondary/60 border border-border text-sm outline-none focus:border-primary"
                  />
                  <textarea
                    data-testid="lead-mensaje"
                    value={lead.mensaje}
                    onChange={(e) => setLead((l) => ({ ...l, mensaje: e.target.value }))}
                    placeholder="Cuéntenos brevemente su situación (opcional)"
                    rows={2}
                    className="w-full px-3 py-2 rounded-lg bg-secondary/60 border border-border text-sm outline-none focus:border-primary resize-none"
                  />
                  <div className="flex items-center gap-2">
                    <button
                      type="submit"
                      data-testid="lead-submit"
                      disabled={leadLoading}
                      className="flex-1 h-10 rounded-full bg-primary text-primary-foreground text-sm font-medium flex items-center justify-center gap-1.5 disabled:opacity-50"
                    >
                      {leadLoading ? "Enviando…" : "Enviar mis datos"}
                    </button>
                    <button
                      type="button"
                      data-testid="lead-dismiss"
                      onClick={() => setShowLead(false)}
                      className="h-10 px-3 rounded-full border border-border text-xs text-muted-foreground hover:bg-secondary"
                    >
                      Ahora no
                    </button>
                  </div>
                </form>
              )}
            </div>

            {/* Input */}
            <form
              onSubmit={send}
              className="p-3 border-t border-border bg-white flex items-center gap-2 shrink-0"
            >
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Escribe tu mensaje…"
                className="flex-1 h-11 px-4 rounded-full bg-secondary/60 border border-border text-sm outline-none focus:border-primary"
              />
              <button
                type="submit"
                data-testid="chat-send"
                disabled={loading || !input.trim()}
                aria-label="Enviar"
                className="h-11 w-11 rounded-full bg-primary text-primary-foreground flex items-center justify-center disabled:opacity-50 transition-opacity"
              >
                <Send size={18} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
